import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Table,
  Text,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Pagination,
  Loader,
  Center,
  Avatar,
  Tooltip,
  Alert,
  Select,
  Modal,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCheck, IconX, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  useMeQuery,
  useAllActiveFranchisesWithStaffQuery,
  usePendingFranchiseApprovalsQuery,
  useSetTransactionApprovalStatusMutation,
  ApprovalStatus,
} from "generated/graphql";
import { formatDistanceToNow } from "date-fns";
import {
  getTransactionTypeLabel,
  getTransactionTypeColor,
  getApprovalSummary,
} from "../TransactionManagerHub/transactionUtils";

const PAGE_SIZE = 10;

/**
 * Hook to get franchises managed by the current user
 */
function useUserFranchises() {
  const { data: meData, loading: meLoading, error: meError } = useMeQuery();
  const {
    data: franchisesData,
    loading: franchisesLoading,
    error: franchisesError,
  } = useAllActiveFranchisesWithStaffQuery();

  const userId = meData?.me?.userData?.id;

  const managedFranchises = useMemo(() => {
    const items = franchisesData?.franchises?.items;
    if (!userId || !items || !Array.isArray(items)) return [];

    return items
      .filter((f) => f.GM?.id === userId || f.AGM?.id === userId)
      .map((f) => ({
        id: f.id,
        name: f.name,
        prefix: f.prefix ?? null,
        logoUrl: f.logoUrl ?? null,
        role: f.GM?.id === userId ? ("GM" as const) : ("AGM" as const),
      }));
  }, [userId, franchisesData]);

  return {
    userId,
    franchises: managedFranchises,
    loading: meLoading || franchisesLoading,
    error: meError || franchisesError,
  };
}

/**
 * FranchisePendingApprovals
 *
 * Lists transactions pending approval from the GM's franchise.
 * Allows GMs to approve or reject transactions involving their franchise.
 */
export default function FranchisePendingApprovals() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { userId, franchises, loading: userLoading, error: userError } = useUserFranchises();

  // State for franchise selection (if user manages multiple)
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string | null>(null);
  const activeFranchiseId = selectedFranchiseId || franchises[0]?.id;

  const [activePage, setActivePage] = useState(1);

  const { data, loading, error, refetch } = usePendingFranchiseApprovalsQuery({
    variables: {
      franchiseId: activeFranchiseId ?? "",
      skip: (activePage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    skip: !activeFranchiseId,
    fetchPolicy: "cache-and-network",
  });

  // Approval modal state
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedApproval, setSelectedApproval] = useState<{
    id: string;
    action: "approve" | "reject";
    transactionType: string;
  } | null>(null);
  const [approvalNotes, setApprovalNotes] = useState("");

  const [setApprovalStatus, { loading: approvalLoading }] =
    useSetTransactionApprovalStatusMutation();

  const transactions = useMemo(() => {
    const items = data?.pendingFranchiseApprovals?.items;
    if (!items || !Array.isArray(items)) return [];

    return items.map((t) => {
      // Find the approval that belongs to this franchise
      const approvals = t.transactionApprovals ?? [];
      const franchiseApproval = approvals.find(
        (a) => a.isFranchiseStaffApproval && a.approvingFranchise?.id === activeFranchiseId
      );
      return {
        id: t.id,
        type: t.transactionType,
        dateSubmitted: t.dateSubmitted,
        submitter: t.submitter,
        submittingFranchise: t.submittingFranchise,
        tradePartnerFranchise: t.tradePartnerFranchise,
        approvals: approvals,
        items: t.transactionItems ?? [],
        notes: t.notes,
        franchiseApprovalId: franchiseApproval?.id,
      };
    });
  }, [data, activeFranchiseId]);

  const totalCount = data?.pendingFranchiseApprovals?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  function handleApprovalClick(
    approvalId: string,
    action: "approve" | "reject",
    transactionType: string
  ) {
    setSelectedApproval({ id: approvalId, action, transactionType });
    setApprovalNotes("");
    open();
  }

  async function handleConfirmApproval() {
    if (!selectedApproval) return;

    // Require notes for rejections
    if (selectedApproval.action === "reject" && !approvalNotes.trim()) {
      notifications.show({
        title: "Notes Required",
        message: "Please provide a reason for rejecting this transaction",
        color: "yellow",
      });
      return;
    }

    try {
      await setApprovalStatus({
        variables: {
          input: {
            id: selectedApproval.id,
            approvalStatus:
              selectedApproval.action === "approve"
                ? ApprovalStatus.Approved
                : ApprovalStatus.Rejected,
            notes: approvalNotes || undefined,
          },
        },
      });

      notifications.show({
        title: selectedApproval.action === "approve" ? "Approved" : "Rejected",
        message: `Transaction has been ${selectedApproval.action === "approve" ? "approved" : "rejected"}.`,
        color: selectedApproval.action === "approve" ? "green" : "red",
      });

      close();
      refetch();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: `Failed to ${selectedApproval.action} transaction.`,
        color: "red",
      });
    }
  }

  if (userLoading) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (userError) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load your franchise information. {userError.message}
        </Alert>
      </Container>
    );
  }

  if (franchises.length === 0) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="No Franchise Assigned" color="yellow">
          You are not currently assigned as GM or AGM of any franchise.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/gm-hub")}
          size="sm"
        >
          Back to GM Hub
        </Button>
      </Group>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
              Pending Franchise Approvals
            </Title>
            <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
              Transactions awaiting your franchise's approval.
            </Text>
          </div>

          {franchises.length > 1 && (
            <Select
              label="Franchise"
              placeholder="Select franchise"
              data={franchises.map((f) => ({
                value: f.id,
                label: f.prefix ? `[${f.prefix}] ${f.name}` : f.name,
              }))}
              value={activeFranchiseId}
              onChange={(value) => {
                setSelectedFranchiseId(value);
                setActivePage(1);
              }}
              size="sm"
              w={250}
            />
          )}
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Submitted By</Table.Th>
                <Table.Th>From / To</Table.Th>
                <Table.Th>Submitted</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && transactions.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="red">
                      Failed to load pending transactions. {error.message}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : transactions.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      No pending transactions requiring your approval.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                transactions.map((t) => {
                  const approvalSummary = getApprovalSummary(t.approvals);
                  return (
                    <Table.Tr key={t.id}>
                      <Table.Td>
                        <Badge color={getTransactionTypeColor(t.type)} variant="light">
                          {getTransactionTypeLabel(t.type)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Avatar src={t.submitter?.avatarUrl} size="sm" radius="xl" />
                          <Text size="sm">{t.submitter?.name || "Unknown"}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          {t.submittingFranchise?.logoUrl && (
                            <Avatar src={t.submittingFranchise.logoUrl} size="sm" radius="sm" />
                          )}
                          <Text size="sm">
                            {t.submittingFranchise?.prefix
                              ? `[${t.submittingFranchise.prefix}]`
                              : t.submittingFranchise?.name || "—"}
                          </Text>
                          {t.tradePartnerFranchise && (
                            <>
                              <Text size="xs" c="dimmed">
                                ↔
                              </Text>
                              <Text size="sm">
                                {t.tradePartnerFranchise.prefix
                                  ? `[${t.tradePartnerFranchise.prefix}]`
                                  : t.tradePartnerFranchise.name}
                              </Text>
                            </>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={new Date(t.dateSubmitted).toLocaleString()}>
                          <Text size="sm">
                            {formatDistanceToNow(new Date(t.dateSubmitted), { addSuffix: true })}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Badge color="green" variant="light" size="sm">
                            {approvalSummary.approved}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            /
                          </Text>
                          <Badge color="orange" variant="light" size="sm">
                            {approvalSummary.pending}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {t.franchiseApprovalId ? (
                          <Group gap="xs">
                            <Tooltip label="Approve">
                              <Button
                                size="xs"
                                variant="light"
                                color="green"
                                onClick={() =>
                                  handleApprovalClick(
                                    t.franchiseApprovalId!,
                                    "approve",
                                    getTransactionTypeLabel(t.type)
                                  )
                                }
                                disabled={approvalLoading}
                              >
                                <IconCheck size={14} />
                              </Button>
                            </Tooltip>
                            <Tooltip label="Reject">
                              <Button
                                size="xs"
                                variant="light"
                                color="red"
                                onClick={() =>
                                  handleApprovalClick(
                                    t.franchiseApprovalId!,
                                    "reject",
                                    getTransactionTypeLabel(t.type)
                                  )
                                }
                                disabled={approvalLoading}
                              >
                                <IconX size={14} />
                              </Button>
                            </Tooltip>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            N/A
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Text size="sm" c={isDark ? "gray.5" : "gray.6"} className="text-center sm:text-left">
            Showing {transactions.length} of {totalCount} pending transaction
            {totalCount === 1 ? "" : "s"}
          </Text>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={activePage}
              onChange={(page) => {
                setActivePage(page);
                refetch({
                  skip: (page - 1) * PAGE_SIZE,
                  take: PAGE_SIZE,
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="sm"
              className="justify-center sm:justify-end"
            />
          )}
        </div>
      </div>

      {/* Approval Confirmation Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          selectedApproval?.action === "approve"
            ? "Approve Transaction"
            : "Reject Transaction"
        }
        centered
      >
        <div className="space-y-4">
          <Text size="sm">
            Are you sure you want to{" "}
            <strong>{selectedApproval?.action === "approve" ? "approve" : "reject"}</strong> this{" "}
            {selectedApproval?.transactionType} transaction?
          </Text>
          <Textarea
            label={selectedApproval?.action === "reject" ? "Reason for Rejection (required)" : "Notes (optional)"}
            placeholder={
              selectedApproval?.action === "reject"
                ? "Please explain why this transaction is being rejected..."
                : "Add any notes about this approval..."
            }
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.currentTarget.value)}
            minRows={2}
            required={selectedApproval?.action === "reject"}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              color={selectedApproval?.action === "approve" ? "green" : "red"}
              onClick={handleConfirmApproval}
              loading={approvalLoading}
            >
              {selectedApproval?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </Group>
        </div>
      </Modal>
    </Container>
  );
}
